import { MediaType } from '@signage-player/data-store';
import { DownloadResultCode, } from '@signage-player/contracts';
import { EventListService, monotonicMs } from '@signage-player/shared-logic';
const DOWNLOADED_KEYS_KEY = 'sp:downloaded-media-keys';
const LEGACY_DOWNLOADED_IDS_KEY = 'sp:downloaded-media-ids';
const DEFAULT_TIMEOUT_MS = 30000;
const MAX_RETRIES = 3;
/**
 * `crypto.randomUUID` is only exposed in secure contexts (HTTPS / localhost).
 * `crypto.getRandomValues` works on plain http://* origins too; build the v4
 * UUID by hand so the web port works on insecure dev origins.
 */
function randomUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}
/**
 * Phase 1 browser media control. Relies on the browser's HTTP cache + server
 * Cache-Control headers — fetch() warms the cache, and the renderer's
 * <video src="https://..."> hits the same URL and serves from cache.
 *
 * Phase 2 will swap in Service Worker + Cache API behind the same interface.
 */
export class LocalMediaControl {
    constructor(desiredVideoQuality, logger, broadcast) {
        this.desiredVideoQuality = desiredVideoQuality;
        this.logger = logger;
        this.broadcast = broadcast;
        this.downloadedKeys = new Set();
        this.downloadingMedias = new Map();
        this.loadDownloadedKeys();
    }
    async initialize() {
        // No initialization needed; nothing to mount in the browser.
    }
    supportsDownloading(playable) {
        return playable.type === MediaType.VIDEO
            || playable.type === MediaType.IMAGE
            || playable.type === MediaType.AUDIO;
    }
    mediaKey(playable) {
        try {
            const media = EventListService.getMediaFromPlayable(playable, this.desiredVideoQuality);
            if (!media)
                return null;
            return `${playable.id}_${media.quality ?? 'na'}_${media.file_name ?? media.url}`;
        }
        catch {
            return null; // WEB & co. — getMediaFromPlayable throws for non-media playables
        }
    }
    async downloadMedia(playable, force) {
        if (!this.supportsDownloading(playable)) {
            return { code: DownloadResultCode.NOT_SUPPORTED };
        }
        const mediaKey = this.mediaKey(playable);
        if (mediaKey && this.downloadedKeys.has(mediaKey) && !force) {
            return { code: DownloadResultCode.ALREADY_DOWNLOADED };
        }
        if (this.downloadingMedias.has(playable.id)) {
            return { code: DownloadResultCode.DOWNLOADING };
        }
        const media = EventListService.getMediaFromPlayable(playable, this.desiredVideoQuality);
        if (!media || typeof media.url !== 'string' || media.url.length === 0) {
            return { code: DownloadResultCode.ERROR, error: `No media URL for playable ${playable.id}` };
        }
        const downloadId = randomUUID();
        const abortController = new AbortController();
        this.downloadingMedias.set(playable.id, {
            playable,
            progress: 0,
            abortController,
            lastProgressAtMono: monotonicMs(),
            downloadId,
        });
        let bustCache = false;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            const owner = this.downloadingMedias.get(playable.id);
            if (!owner || owner.downloadId !== downloadId) {
                return { code: DownloadResultCode.ERROR, error: 'Cancelled or superseded' };
            }
            try {
                const response = await fetch(media.url, {
                    cache: force || bustCache ? 'reload' : 'force-cache',
                    signal: abortController.signal,
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                // Drain the body so the browser actually populates the HTTP cache.
                const received = await this.drainResponse(response, playable.id, downloadId);
                // received === 0 means a bodyless response — can't verify.
                if (media.size && received > 0 && received !== media.size) {
                    // Wrong bytes may be the cached entry itself — bust the cache on retry.
                    bustCache = true;
                    throw new Error(`Size mismatch: expected ${media.size}, received ${received}`);
                }
                if (mediaKey) {
                    this.downloadedKeys.add(mediaKey);
                    this.persistDownloadedKeys();
                }
                this.releaseIfOwner(playable.id, downloadId);
                return { code: DownloadResultCode.SUCCESS };
            }
            catch (error) {
                if (error?.name === 'AbortError' || abortController.signal.aborted) {
                    this.releaseIfOwner(playable.id, downloadId);
                    return { code: DownloadResultCode.ERROR, error: 'Cancelled' };
                }
                this.logger.warning('[LocalMediaControl] Download attempt failed', {
                    playableId: playable.id, attempt, error: error?.message ?? String(error),
                });
                if (attempt === MAX_RETRIES) {
                    this.releaseIfOwner(playable.id, downloadId);
                    return { code: DownloadResultCode.ERROR, error: error?.message ?? String(error) };
                }
                await new Promise(r => setTimeout(r, 1000 * attempt));
            }
        }
        return { code: DownloadResultCode.ERROR, error: 'Download failed' };
    }
    // A superseding download must not have its entry deleted by a stale promise.
    releaseIfOwner(playableId, downloadId) {
        if (this.downloadingMedias.get(playableId)?.downloadId === downloadId) {
            this.downloadingMedias.delete(playableId);
        }
    }
    async drainResponse(response, playableId, downloadId) {
        if (!response.body)
            return 0;
        const total = Number(response.headers.get('Content-Length') ?? 0);
        const reader = response.body.getReader();
        let received = 0;
        let done = false;
        while (!done) {
            const chunk = await reader.read();
            done = chunk.done;
            if (chunk.value)
                received += chunk.value.byteLength;
            const owner = this.downloadingMedias.get(playableId);
            if (owner && owner.downloadId === downloadId) {
                owner.lastProgressAtMono = monotonicMs();
                owner.progress = total > 0 ? (received / total) * 100 : 0;
                this.broadcast.broadcast('media:downloadProgress', owner.progress, received, total);
            }
        }
        return received;
    }
    isDownloaded(playable) {
        const key = this.mediaKey(playable);
        return key !== null && this.downloadedKeys.has(key);
    }
    isDownloading(playable) {
        return this.downloadingMedias.has(playable.id);
    }
    isDownloadingSomething() {
        return this.downloadingMedias.size > 0;
    }
    cancelDownload(playable) {
        const entry = this.downloadingMedias.get(playable.id);
        if (entry) {
            entry.abortController.abort();
            this.downloadingMedias.delete(playable.id);
        }
    }
    getDownloadProgress(playable) {
        return this.downloadingMedias.get(playable.id)?.progress ?? 0;
    }
    getDownloadedMedias() {
        // We don't keep playable objects after success in Phase 1 — return empty.
        // SignageApplication only uses this for telemetry; emptiness is acceptable.
        return [];
    }
    getDownloadedPath(playable) {
        const media = EventListService.getMediaFromPlayable(playable, this.desiredVideoQuality);
        if (!media || typeof media.url !== 'string') {
            throw new Error(`No URL for playable ${playable.id}`);
        }
        return media.url;
    }
    getDownloadedMediaName(playable) {
        const media = EventListService.getMediaFromPlayable(playable, this.desiredVideoQuality);
        return media?.file_name ?? `playable-${playable.id}`;
    }
    async checkForUnusedMedias(_library, _deleteUnused) {
        // HTTP cache is browser-managed and opaque; we can't enumerate it from JS.
        return [];
    }
    async checkStorageLimit(_upcoming, storageLimitBytes) {
        if (!navigator.storage?.estimate)
            return true;
        try {
            const { usage = 0 } = await navigator.storage.estimate();
            if (usage > storageLimitBytes) {
                this.logger.warning('[LocalMediaControl] Storage usage exceeds limit (HTTP cache eviction is browser-managed)', {
                    usage, storageLimitBytes,
                });
                return false;
            }
            return true;
        }
        catch {
            return true;
        }
    }
    checkStuckDownloads(retry) {
        const now = monotonicMs();
        for (const [id, entry] of this.downloadingMedias) {
            if (now - entry.lastProgressAtMono > DEFAULT_TIMEOUT_MS) {
                this.logger.warning('[LocalMediaControl] Download stuck, cancelling', { playableId: id });
                this.cancelDownload(entry.playable);
                if (retry) {
                    setTimeout(() => {
                        if (!this.downloadingMedias.has(id)) {
                            this.downloadMedia(entry.playable, false);
                        }
                    }, 5000);
                }
            }
        }
    }
    async verifyMedia(playable) {
        try {
            const media = EventListService.getMediaFromPlayable(playable, this.desiredVideoQuality);
            if (!media || typeof media.url !== 'string' || media.url.length === 0)
                return false;
            const head = await fetch(media.url, { method: 'HEAD' });
            if (!head.ok)
                return false;
            const lenHeader = head.headers.get('Content-Length');
            if (lenHeader === null || !media.size)
                return true; // unknown size — can't disprove
            return Number(lenHeader) === media.size;
        }
        catch (err) {
            this.logger.error('[LocalMediaControl] verifyMedia failed', { playableId: playable.id, error: err });
            return false;
        }
    }
    async verifyDownloadedMediaIntegrity() {
        // HTTP cache integrity is browser-managed; nothing we can verify locally.
        return 0;
    }
    async deleteMedia(playable) {
        // Browser HTTP cache can't be selectively evicted in Phase 1. Drop the
        // membership so the app re-downloads (and the cache will eventually be
        // refreshed by Cache-Control rules).
        const key = this.mediaKey(playable);
        const had = key !== null && this.downloadedKeys.delete(key);
        if (had)
            this.persistDownloadedKeys();
        return true;
    }
    async destroy() {
        for (const entry of this.downloadingMedias.values()) {
            entry.abortController.abort();
        }
        this.downloadingMedias.clear();
    }
    loadDownloadedKeys() {
        try {
            localStorage.removeItem(LEGACY_DOWNLOADED_IDS_KEY);
            const raw = localStorage.getItem(DOWNLOADED_KEYS_KEY);
            if (!raw)
                return;
            const parsed = JSON.parse(raw);
            if (parsed && Array.isArray(parsed.value)) {
                for (const key of parsed.value) {
                    if (typeof key === 'string')
                        this.downloadedKeys.add(key);
                }
            }
        }
        catch {
            // best-effort
        }
    }
    persistDownloadedKeys() {
        try {
            const envelope = { value: Array.from(this.downloadedKeys), expiresAt: null };
            localStorage.setItem(DOWNLOADED_KEYS_KEY, JSON.stringify(envelope));
        }
        catch (err) {
            this.logger.error('[LocalMediaControl] Failed to persist downloaded keys', { error: err });
        }
    }
}
export default LocalMediaControl;
//# sourceMappingURL=LocalMediaControl.js.map